import axios, { getAdapter } from 'axios';
import { getAdminToken, clearAdminSession } from '../utils/authSessionStorage';
import { encryptPayload, decryptIfNeeded } from '../utils/crypto';


const BASE_URL =
  process.env.REACT_APP_API_BASE_URL || 'https://dev.settleai.app/api';
const apiEndpoint = "/GetSettled/Admin";

/** Backend expects this on login and admin API calls. Override with `REACT_APP_X_SESSION_HEADER`. */
const getAdminXSessionHeader = () =>
  process.env.REACT_APP_X_SESSION_HEADER ?? 'admin-db05a764fdc044249c6a341afb6f7bfa';

/** Customer verification endpoints use a customer session header. Override with `REACT_APP_CUSTOMER_VERIFICATION_X_SESSION_HEADER`. */
const getCustomerVerificationXSessionHeader = () =>
  process.env.REACT_APP_CUSTOMER_VERIFICATION_X_SESSION_HEADER ??
  'customer-9319737c11894da8a94643d11ec56e7d';

const baseHttpAdapter = getAdapter(['xhr', 'http', 'fetch']);

/**
 * Concurrent identical GETs share one underlying request (e.g. React StrictMode
 * remounting effects, or two effects firing the same URL at once).
 * Opt out per request: `api.get(url, { skipGetDedupe: true })`.
 */
const inflightGetByKey = new Map();

function dedupeGetAdapter(config) {
  const method = String(config.method || 'get').toLowerCase();
  if (method !== 'get' || config.skipGetDedupe) {
    return baseHttpAdapter(config);
  }
  let key;
  try {
    key = axios.getUri(config);
  } catch {
    return baseHttpAdapter(config);
  }
  const existing = inflightGetByKey.get(key);
  if (existing) {
    return existing;
  }
  const p = Promise.resolve(baseHttpAdapter(config)).finally(() => {
    inflightGetByKey.delete(key);
  });
  inflightGetByKey.set(key, p);
  return p;
}

const api = axios.create({
  baseURL: BASE_URL,
  adapter: dedupeGetAdapter,
});

// Add apiEndpoint to all admin requests
api.interceptors.request.use((config) => {
  // Only add apiEndpoint if it's not an authentication request
  if (!config.skipAdminPrefix && !config.url.includes('Authentication')) {
    config.url = `${apiEndpoint}${config.url}`;
  }
  return config;
});

// Add token and session header to admin API requests (both api and axios instances)
[api, axios].forEach((instance) => {
  instance.interceptors.request.use((config) => {
    const token = getAdminToken();
    if (token) {
      if (typeof config.headers?.set === 'function') {
        config.headers.set('Authorization', 'Bearer ' + token);
      } else if (config.headers) {
        config.headers.Authorization = 'Bearer ' + token;
      }
    }

    const sessionVal = config.xSessionHeader ?? getAdminXSessionHeader();
    if (config.headers) {
      if (typeof config.headers.set === 'function') {
        config.headers.set('X-Session-Header', sessionVal);
        config.headers.set('x-session-header', sessionVal);
      } else {
        config.headers['X-Session-Header'] = sessionVal;
        config.headers['x-session-header'] = sessionVal;
      }
    }
    return config;
  });
});

// Payload encryption and decryption interceptors
const instances = [api, axios];
instances.forEach((instance) => {
  instance.interceptors.request.use((config) => {
    const keyStr = process.env.REACT_APP_PAYLOAD_ENCRYPTION_KEY;
    if (keyStr && config.data && !(config.data instanceof FormData)) {
      try {
        const encrypted = encryptPayload(config.data);
        config.data = encrypted;
        if (config.headers) {
          if (typeof config.headers.set === 'function') {
            config.headers.set('Content-Type', 'application/json');
          } else {
            config.headers['Content-Type'] = 'application/json';
          }
        }
      } catch (err) {
        console.error('Request encryption failed:', err);
      }
    }
    return config;
  }, (error) => {
    return Promise.reject(error);
  });

  instance.interceptors.response.use((response) => {
    if (response && response.data) {
      response.data = decryptIfNeeded(response.data);
    }
    return response;
  }, (error) => {
    if (error && error.response && error.response.data) {
      error.response.data = decryptIfNeeded(error.response.data);
    }
    if (error?.response?.status === 401) {
      clearAdminSession();
      if (typeof window !== 'undefined' && window.location.pathname !== '/login' && window.location.pathname !== '/super-admin-login') {
        window.location.href = '/login';
      }
    }
    return Promise.reject(error);
  });
});

export const customerSupportAPI = {
  /** Optional `options.name` (trimmed) → `name` query — not `title`. */
  getCustomerSupports: (cityId = null, page = 0, pageSize = 10, options = {}) => {
    let url = `/CustomerSupports?Page=${page}&PageSize=${pageSize}`;
    if (cityId != null && cityId !== '') {
      url += `&CityId=${encodeURIComponent(cityId)}`;
    }
    const q = options.name != null ? String(options.name).trim() : '';
    if (q) {
      url += '&name=' + encodeURIComponent(q);
    }
    return api.get(url);
  },
  getCustomerSupport: (id) => api.get(`/CustomerSupport?SupportId=${id}`),
  createCustomerSupport: (data) => api.post('/CustomerSupport', data),
  updateCustomerSupport: (data) => api.post('/CustomerSupport', data),
  deleteCustomerSupport: (id) => api.delete(`/CustomerSupport?SupportId=${id}`),
};

export const districtsAPI = {
  /** Optional text filter: `options.search` / `options.title` → `title` query (URL-encoded). `CityId` omitted when `cityId` is empty. */
  getDistricts: (cityId, page = 0, pageSize = 10, options = {}) => {
    let url = `/Districts?Page=${page}&PageSize=${pageSize}`;
    if (cityId != null && cityId !== '') {
      url += `&CityId=${encodeURIComponent(cityId)}`;
    }
    const raw = options.title != null ? options.title : options.search;
    const q = raw != null ? String(raw).trim() : '';
    if (q) {
      url += `&title=${encodeURIComponent(q)}`;
    }
    return api.get(url);
  },
  getDistrict: (id) => api.get(`/District?DistrictId=${id}`),
  createDistrict: (data) => api.post('/District', data),
  updateDistrict: (data) => api.post('/District', data),
  deleteDistrict: (id) => api.delete(`/District?DistrictId=${id}`),
};

export const eventCategoriesAPI = {
  getEventCategories: (cityId, page = 0, pageSize = 10) => api.get(`/EventCategorys?CityId=${cityId}&Page=${page}&PageSize=${pageSize}`),
  getEventCategory: (id) => api.get(`/EventCategory?CategoryId=${id}`),
  createEventCategory: (data) => api.post('/EventCategory', data),
  updateEventCategory: (data) => api.post('/EventCategory', data),
  deleteEventCategory: (id) => api.delete(`/EventCategory?EventCategoryId=${id}`),
};

// Event Types API (Swagger: GET/POST/PUT/DELETE EventType)
export const eventTypesAPI = {
  getEventTypes: () => api.get('/EventTypes'),
  getEventType: (id) => api.get(`/EventType?Id=${id}`),
  createEventType: (data) => api.post('/EventType', data),
  updateEventType: (data) => api.put('/EventType', data),
  deleteEventType: (id) => api.delete(`/EventType?EventTypeId=${id}`),
};


export const promotionTypesAPI = {
  getPromotionTypes: () => api.get('/PromotionTypes'),
  getPromotionType: (id) => api.get(`/PromotionType?Id=${id}`),
  createPromotionType: (data) => api.post('/PromotionType', data),
  updatePromotionType: (data) => api.put('/PromotionType', data),
  deletePromotionType: (id) => api.delete(`/PromotionType?PromotionTypeId=${id}`),
};

export const accommodationAPI = {
  /**
   * GET /Accomodations — query string built explicitly (same pattern as Events/Promotions) so the
   * admin URL interceptor always receives a full path + query. Optional: title, isActive.
   */
  getAccommodations: (cityId, page = 0, pageSize = 10, options = {}) => {
    let url = `/Accomodations?CityId=${encodeURIComponent(cityId)}&Page=${page}&PageSize=${pageSize}`;
    const title = options.title != null ? String(options.title).trim() : '';
    if (title) {
      url += `&title=${encodeURIComponent(title)}`;
    }
    if (options.isActive === true || options.isActive === false) {
      url += `&isActive=${options.isActive}`;
    }
    return api.get(url);
  },
  getAccommodation: (id) => api.get(`/Accomodation?AccomodationId=${id}`),
  createAccommodation: (data) => api.post('/Accomodation', data),
  /** Multipart FormData: existing URLs + new files — allow large bodies for many images */
  updateAccommodation: (data) =>
    api.put('/Accomodation', data, {
      maxBodyLength: Infinity,
      maxContentLength: Infinity
    }),
  deleteAccommodation: (id) => api.delete(`/Accomodation?AccomodationId=${id}`),
  updateAccomodationApproval: (data) => api.post('/Accomodation/UpdateApprovalStatus', data),
  aiAutofill: (data) =>
    api.post('/Accomodation/ai-autofill', typeof data === 'string' ? { rawText: data } : data),
};

export const accommodationFacilitiesAPI = {
  /** Title / text filter: sent as `Search` on GET AccomodationFacilitys (URL-encoded). */
  getFacilities: (page = 0, pageSize = 10, search = '') => {
    let url = `/AccomodationFacilitys?Page=${page}&PageSize=${pageSize}`;
    const q = search != null ? String(search).trim() : '';
    if (q) {
      url += `&Search=${encodeURIComponent(q)}`;
    }
    return api.get(url);
  },
  getFacility: (id) => api.get(`/AccomodationFacility?FacilityId=${id}`),
  createFacility: (data) => api.post('/AccomodationFacility', data),
  updateFacility: (data) => api.post('/AccomodationFacility', data),
  deleteFacility: (id) => api.delete(`/AccomodationFacility?AccomodationFacilityId=${id}`),
};

export const accommodationTypesAPI = {
  getAccommodationTypes: (page = 0, pageSize = 10) => api.get(`/AccomodationTypes?Page=${page}&PageSize=${pageSize}`),
  getAccommodationType: (id) => api.get(`/AccomodationTypes?AccomodationTypeId=${id}`),
  createAccommodationType: (data) => api.post('/AccomodationType', data),
  updateAccommodationType: (data) => api.put('/AccomodationType', data),
  deleteAccommodationType: (id) => api.delete(`/AccomodationType?AccomodationTypeId=${id}`),
};

export const cityRegistrationAPI = {
  getCityRegistrations: (cityId, page = 0, pageSize = 10) => api.get(`/CityRegistration?CityId=${cityId}&Page=${page}&PageSize=${pageSize}`),
  getCityRegistration: (id) => api.get(`/CityRegistration?RegistrationId=${id}`),
  createCityRegistration: (data) => api.post('/CityRegistration', data),
  updateCityRegistration: (data) => api.post('/CityRegistration', data),
  deleteCityRegistration: (id) => api.delete(`/CityRegistration?CityId=${id}`),
};

export const categoriesAPI = {
  /**
   * @param {string|null|undefined} cityId - when set, adds `CityId` to the query.
   * @param {object} [options] - optional `{ title }` or `{ search }` (trimmed) → `title` query param.
   */
  getCategories: (cityId = null, page = 0, pageSize = 100, options = {}) => {
    let url = `/Categorys?Page=${page}&PageSize=${pageSize}`;
    if (cityId != null && cityId !== '') {
      url += `&CityId=${encodeURIComponent(cityId)}`;
    }
    const raw =
      options.title != null ? options.title : options.name != null ? options.name : options.search;
    const q = raw != null ? String(raw).trim() : '';
    if (q) {
      url += `&title=${encodeURIComponent(q)}`;
    }
    if (options.hasContact === true || options.hasContact === 'true') {
      url += `&hasContact=true`;
    }
    return api.get(url);
  },
  getCategory: (id) => api.get(`/Category?CategoryId=${id}`),
  createCategory: (data) => api.post('/Category', data),
  updateCategory: (data) => api.post('/Category', data),
  deleteCategory: (id) => api.delete(`/Category?CategoryId=${id}`),
};

export const usersAPI = {
  /** Optional `options.name` or `options.title` (trimmed) → `name` query (not `time` or other params). */
  getUsers: (page = 0, pageSize = 10, options = {}) => {
    let url = `/Users?Page=${page}&PageSize=${pageSize}`;
    const raw = options.name != null ? options.name : options.title;
    const q = raw != null ? String(raw).trim() : '';
    if (q) {
      url += `&name=${encodeURIComponent(q)}`;
    }
    return api.get(url);
  },
  getUser: (id) => api.get(`/User?UserId=${id}`),
  createUser: (data) => api.post('/User', data),
  updateUser: (data) => api.post('/User', data),
  deleteUser: (id) => api.delete(`/User/Purge?UserId=${id}`),
};

export const notificationsAPI = {
  getNotifications: (page = 1, pageSize = 20, language = 'en') => {
    let url = `/notifications?page=${Number(page)}&pageSize=${Number(pageSize)}`;
    if (language === 'de') {
      url += `&language=de`;
    }
    return api.get(url);
  },
  markAsRead: (id) => api.post(`/notifications/${id}/read`),
  markAllAsRead: () => api.post('/notifications/read-all'),
};

/** Customer registration (admin): multipart create/update, preview GET, document DELETE */
export const customerAPI = {
  registerFullyVerified: (formData) =>
    api.post('/Customer/RegisterFullyVerified', formData, {
      maxBodyLength: Infinity,
      maxContentLength: Infinity,
    }),
  getRegistrationPreview: (userId) => api.get(`/Customer/RegistrationPreview/${userId}`),
  updateRegistrationPreview: (userId, formData) =>
    api.put(`/Customer/RegistrationPreview/${userId}`, formData, {
      maxBodyLength: Infinity,
      maxContentLength: Infinity,
    }),
  deleteRegistrationDocument: (userId, documentId) =>
    api.delete(`/Customer/RegistrationPreview/${userId}/Documents/${documentId}`),
};

export const languageSupportAPI = {
  /** Optional `options.name` (trimmed) → `name` query param — not `title`. */
  getLanguages: (page = 0, pageSize = 10, options = {}) => {
    let url = `/Languages?Page=${page}&PageSize=${pageSize}`;
    const q = options.name != null ? String(options.name).trim() : '';
    if (q) {
      url += `&name=${encodeURIComponent(q)}`;
    }
    return api.get(url);
  },
  getLanguage: (id) => api.get(`/Language?LanguageId=${id}`),
  createLanguage: (data) => api.post('/Language', data),
  updateLanguage: (data) => api.post('/Language', data),
  deleteLanguage: (id) => api.delete(`/Language?LanguageId=${id}`),
};

// Helper: filter list to only enabled items (isEnabled !== false). Use for dropdowns across all screens.
const filterEnabled = (payload) =>
  Array.isArray(payload) ? payload.filter((item) => item.isEnabled !== false) : payload;

export const citiesAPI = {
  getCities: (page = 0, pageSize = 500) =>
    api.get(`/Cities?Page=${page}&PageSize=${pageSize}`).then((response) => {
      if (response?.data?.success === true && Array.isArray(response.data.payload)) {
        response.data.payload = filterEnabled(response.data.payload);
      }
      return response;
    }),
  getCitiess: () => api.get('/Cities?Page=0&PageSize=10'),
  getCity: (id) => api.get(`/City?CityId=${id}`),
  createCity: (data) => api.post('/City', data),
  updateCity: (id, data) => api.put(`/City?CityId=${id}`, data),
  deleteCity: (id) => api.delete(`/City?CityId=${id}`),
};

export const eventsAPI = {
  /** Optional `title` and `isActive` query params (same pattern as GET /Promotions). */
  getEvents: (cityId = null, page = 0, pageSize = 10, options = {}) => {
    let url = `/Events?Page=${page}&PageSize=${pageSize}`;
    if (cityId != null && cityId !== '' && cityId !== 'all') {
      url += `&CityId=${encodeURIComponent(cityId)}`;
    }
    const title = options.title != null ? String(options.title).trim() : '';
    if (title) {
      url += `&title=${encodeURIComponent(title)}`;
    }
    if (options.isActive === true || options.isActive === false) {
      url += `&isActive=${options.isActive}`;
    }
    return api.get(url);
  },
  /** GET /Events/Trending - fetch trending events list */
  getTrendingEvents: (cityId = null, page = 0, pageSize = 10, options = {}) => {
    let url = `/Events/Trending?Page=${page}&PageSize=${pageSize}`;
    if (cityId != null && cityId !== '' && cityId !== 'all') {
      url += `&CityId=${encodeURIComponent(cityId)}`;
    }
    const title = options.title != null ? String(options.title).trim() : '';
    if (title) {
      url += `&title=${encodeURIComponent(title)}`;
    }
    if (options.isActive === true || options.isActive === false) {
      url += `&isActive=${options.isActive}`;
    }
    return api.get(url);
  },
  getEvent: (id) => api.get(`/Event?EventId=${id}`),
  createEvent: (data) => api.post('/Event', data),
  updateEvent: (data) => api.post('/Event', data),
  deleteEvent: (id) => api.delete(`/Event?EventId=${id}`),
  updateEventApproval: (data) => api.post('/Event/UpdateApprovalStatus', data),
};

export const promotionsAPI = {
  /** Optional `title` and `isActive` query params (same contract as admin GET /Promotions). */
  getPromotions: (cityId = null, page = 0, pageSize = 10, options = {}) => {
    let url = `/Promotions?Page=${page}&PageSize=${pageSize}`;
    if (cityId != null && cityId !== '') {
      url += `&CityId=${encodeURIComponent(cityId)}`;
    }
    const title = options.title != null ? String(options.title).trim() : '';
    if (title) {
      url += `&title=${encodeURIComponent(title)}`;
    }
    if (options.isActive === true || options.isActive === false) {
      url += `&isActive=${options.isActive}`;
    }
    return api.get(url);
  },
  getPromotion: (id) => api.get(`/Promotion?PromotionId=${id}`),
  getPromotionByTypeId: (promotionTypeId) => api.get(`/Promotion?PromotionTypeId=${promotionTypeId}`),
  createPromotion: (data) => api.post('/Promotion', data),
  updatePromotion: (data) => api.post('/Promotion', data),
  deletePromotion: (id) => api.delete(`/Promotion?PromotionId=${id}`),
  updatePromotionApproval: (data) => api.post('/Promotion/UpdateApprovalStatus', data),
};

export const insuranceTypeDetailSummaryAPI = {
  getInsuranceTypeDetailSummarys: (page = 0, pageSize = 10) => api.get(`/InsuranceTypeDetailSummarys?Page=${page}&PageSize=${pageSize}`),
  createInsuranceTypeDetailSummary: (data) => api.post('/InsuranceTypeDetailSummary', data),
  updateInsuranceTypeDetailSummary: (data) => api.post('/InsuranceTypeDetailSummary', data),
  deleteInsuranceTypeDetailSummary: (insuranceTypeId) => api.delete(`/InsuranceTypeDetailSummary?InsuranceTypeId=${insuranceTypeId}`),
};

// -------------------- Job Portals --------------------
export const jobPortalsAPI = {
  /** Optional `options.title` (trimmed) → `title` query param (URL-encoded). */
  getJobPortals: (cityId = null, page = 0, pageSize = 10, options = {}) => {
    let url = `/JobPortals?Page=${page}&PageSize=${pageSize}`;
    if (cityId != null && cityId !== '') {
      url += `&CityId=${encodeURIComponent(cityId)}`;
    }
    const title = options.title != null ? String(options.title).trim() : '';
    if (title) {
      url += `&title=${encodeURIComponent(title)}`;
    }
    return api.get(url);
  },
  createJobPortal: (data) => api.post('/JobPortal', data),
  updateJobPortal: (data) => api.post('/JobPortal', data),
  deleteJobPortal: (id) => api.delete(`/JobPortal?JobPortalId=${id}`),
};


export const contactsAPI = {
  /** Optional `title` and `isActive` query params for server-side list filtering. */
  getContacts: (cityId = null, categoryId = null, page = 0, pageSize = 10, options = {}) => {
    let url = `/Contacts?Page=${page}&PageSize=${pageSize}`;
    if (cityId != null && cityId !== '') {
      url += `&CityId=${encodeURIComponent(cityId)}`;
    }
    if (categoryId != null && categoryId !== '') {
      url += `&CategoryId=${encodeURIComponent(categoryId)}`;
    }
    const title = options.title != null ? String(options.title).trim() : '';
    if (title) {
      url += `&title=${encodeURIComponent(title)}`;
    }
    if (options.isActive === true || options.isActive === false) {
      url += `&isActive=${options.isActive}`;
    }
    return api.get(url);
  },
  getContact: (id) => api.get(`/Contact?ContactId=${id}`),
  createContact: (data) => api.post('/Contact', data),
  updateContact: (data) => api.post('/Contact', data),
  deleteContact: (id) => api.delete(`/Contact?ContactId=${id}`),
  updateContactApproval: (data) => api.post('/Contact/UpdateApprovalStatus', data),
};

export const contactInformationAPI = {
  getContactInformation: (id, categoryId) =>
    api.get(`/ContactInformation?Id=${id}&CategoryId=${categoryId}`),
  getContactInformationByCategory: (categoryId, page = 0, pageSize = 10) =>
    api.get(`/ContactInformation/ByCategory?CategoryId=${categoryId}&Page=${page}&PageSize=${pageSize}`),
  createContactInformation: (data) => api.post('/ContactInformation', data),
  updateContactInformation: (data) =>
    api.put(`/ContactInformation?ContactInformationId=${data.contactInformationId || data.id}&Id=${data.id}&CategoryId=${data.categoryId}`, data),
  deleteContactInformation: (id, categoryId) =>
    api.delete(`/ContactInformation?Id=${id}&CategoryId=${categoryId}`),
  deleteContactInformationTranslation: (contactInformationId, languageCode) =>
    api.delete(`/ContactInformation/Translation?ContactInformationId=${contactInformationId}&LanguageCode=${languageCode}`),
};

export const contactInformationDetailAPI = {
  getContactInformationDetails: (contactId) => api.get(`/ContactInformationDetail?ContactId=${contactId}`),
  getContactInformationDetailsByCategory: (categoryId) => api.get(`/ContactInformationDetail?CategoryId=${categoryId}`),
  getContactInformationDetailById: (id) => api.get(`/ContactInformationDetail/${id}`),
  createContactInformationDetail: (data) => api.post('/ContactInformationDetail', data),
  updateContactInformationDetail: (data) => api.put('/ContactInformationDetail', data),
  deleteContactInformationDetail: (id, categoryId) =>
    api.delete(`/ContactInformationDetail?Id=${id}&CategoryId=${categoryId}`),
  deleteContactInformationDetailTranslation: (contactInformationDetailId, languageCode) =>
    api.delete(`/ContactInformationDetail/Translation?ContactInformationDetailId=${contactInformationDetailId}&LanguageCode=${languageCode}`),
};


export const dashboardAPI = {
  getStatistics: (period = 'year', month = null, year = null) => {
    let url = `/Dashboard/Statistics?period=${period}`;
    if (month != null && month !== '' && month !== 'all') url += `&month=${encodeURIComponent(month)}`;
    if (year != null && year !== '') url += `&year=${encodeURIComponent(year)}`;
    return api.get(url);
  },
  getStatisticsByCity: (cityId, period = 'year', month = null, year = null) => {
    let url = `/Dashboard/Statistics/ByCity?cityId=${cityId}&period=${period}`;
    if (month != null && month !== '' && month !== 'all') url += `&month=${encodeURIComponent(month)}`;
    if (year != null && year !== '') url += `&year=${encodeURIComponent(year)}`;
    return api.get(url);
  },
  getStats: (period = 'year') => api.get(`/dashboard/stats?period=${period}`),
  getRecentActivity: (limit = 10) => api.get(`/Dashboard/Statistics/RecentActivity?limit=${limit}`),
  getCategoryBreakdown: () => api.get('/Dashboard/Statistics/CategoryBreakdown'),
  getModuleHealth: () => api.get('/Dashboard/ModuleHealth'),
};

export const documentsAPI = {
  getDocuments: (cityId = null, serviceId = null, serviceCategoryTypeId = null, page = 0, pageSize = 10) => {
    let url = `/Documents?Page=${page}&PageSize=${pageSize}`;
    if (cityId != null && cityId !== '') url += `&CityId=${Number(cityId)}`;
    if (serviceId != null && serviceId !== '') url += `&ServiceId=${Number(serviceId)}`;
    if (serviceCategoryTypeId != null && serviceCategoryTypeId !== '') url += `&ServiceCategoryTypeId=${Number(serviceCategoryTypeId)}`;
    return api.get(url);
  },
  getDocument: (id) => api.get(`/Document?DocumentId=${id}`),
  createDocument: (data) => api.post('/Documents', data),
  updateDocument: (data) => api.post('/Documents', data),
  deleteDocument: (serviceId, serviceCategoryTypeId) =>
    api.delete(`/Documents?ServiceId=${Number(serviceId)}&ServiceCategoryTypeId=${Number(serviceCategoryTypeId)}`)
};

const customerVerificationRequestConfig = {
  skipAdminPrefix: true,
  xSessionHeader: getCustomerVerificationXSessionHeader(),
  headers: {
    'X-Session-Header': getCustomerVerificationXSessionHeader(),
  },
};

export const customerVerificationAPI = {
  getPendingDocuments: (page = 1, pageSize = 20) =>
    api.get(`/customer/verification/admin/pending?page=${Number(page)}&pageSize=${Number(pageSize)}`, {
      ...customerVerificationRequestConfig,
    }),
  reviewDocument: (data) =>
    api.post('/customer/verification/admin/review', data, {
      ...customerVerificationRequestConfig,
    }),
  reviewDocumentsBulk: (data) =>
    api.post('/customer/verification/admin/review/bulk', data, {
      ...customerVerificationRequestConfig,
    }),
    
  setComplianceChecked: (payload) =>
    api.post('/customer/verification/admin/compliance-checked', payload, {
      ...customerVerificationRequestConfig,
    }),
  checkCompliance: (data) =>
    api.post('/customer/verification/admin/compliance/check', data, {
      ...customerVerificationRequestConfig,
    }),
};

// -------------------- Service Sub Categories --------------------
export const serviceSubCategoriesAPI = {
  /** Optional text filter: `options.search` / `options.title` → `title` query (URL-encoded). */
  getServiceSubCategories: (categoryId = null, page = 0, pageSize = 10, options = {}) => {
    let url = `/SubCategorys?Page=${page}&PageSize=${pageSize}`;
    if (categoryId) {
      url += `&ServiceCategoryId=${encodeURIComponent(categoryId)}`;
    }
    const raw = options.title != null ? options.title : options.search;
    const q = raw != null ? String(raw).trim() : '';
    if (q) {
      url += `&title=${encodeURIComponent(q)}`;
    }
    return api.get(url);
  },
  getServiceSubCategory: (id) => api.get(`/SubCategorys?SubCategoryId=${id}`),
  createServiceSubCategory: (data) => api.post('/SubCategory', data),
  updateServiceSubCategory: (data) => api.post('/SubCategory', data),
  deleteServiceSubCategory: (id) => api.delete(`/SubCategory?SubCategoryId=${id}`),
};

export const recommendationsAPI = {
  getRecommendations: (cityId = null, serviceId = null, serviceCategoryTypeId = null, page = 0, pageSize = 10) => {
    let url = `/Recommendations?Page=${page}&PageSize=${pageSize}`;
    if (cityId) {
      url += `&CityId=${cityId}`;
    }
    if (serviceId) {
      url += `&ServiceId=${serviceId}`;
    }
    if (serviceCategoryTypeId) {
      url += `&ServiceCategoryTypeId=${serviceCategoryTypeId}`;
    }
    return api.get(url);
  },
  getRecommendation: (id) => api.get(`/Recommendation?RecommendationId=${id}`),
  createRecommendation: (data) => api.post('/Recommendations', data),
  updateRecommendation: (data) => api.post('/Recommendations', data),
  deleteRecommendation: (cityId, serviceId, serviceCategoryTypeId) => {
    let url = `/Recommendations?CityId=${cityId}&ServiceId=${serviceId}&ServiceCategoryTypeId=${serviceCategoryTypeId}`;
    return api.delete(url);
  }
};

const legalRequestConfig = {
  skipAdminPrefix: true,
  xSessionHeader: '6d5e4a6f5d0830c692814a506605c32a4e06263a',
  headers: {
    'X-Session-Header': '6d5e4a6f5d0830c692814a506605c32a4e06263a',
  },
};

export const legalAPI = {
  /** 
   * GET /api/[Privacy|Terms]/file/[id] 
   */
  getFileContent: (fileUrl) => api.get(fileUrl, { ...legalRequestConfig }),
  /** 
   * GET /api/Privacy/history?platform=app|customer 
   */
  getPrivacyHistory: (platform) => api.get(`/Privacy/history?platform=${platform}`, { ...legalRequestConfig }),
  /** 
   * GET /api/Terms/history?platform=app|customer 
   */
  getTermsHistory: (platform) => api.get(`/Terms/history?platform=${platform}`, { ...legalRequestConfig }),

  /** 
   * POST /api/Privacy/upload 
   * Multipart: File (html/md), Platform (app/customer), Version, ChangeOfContent 
   */
  uploadPrivacyPolicy: (formData) => api.post('/Privacy/upload', formData, {
    ...legalRequestConfig,
    headers: {
      'Content-Type': 'multipart/form-data',
      ...legalRequestConfig.headers,
    },
  }),
  /** 
   * POST /api/Terms/upload 
   * Multipart: File (html/md), Platform (app/customer), Version, ChangeOfContent 
   */
  uploadTermsAndConditions: (formData) => api.post('/Terms/upload', formData, {
    ...legalRequestConfig,
    headers: {
      'Content-Type': 'multipart/form-data',
      ...legalRequestConfig.headers,
    },
  }),
};



/**
 * Build normalized admin user object for encrypted session storage.
 * Use the user object from login API response (e.g. payload.user or payload).
 */
export const buildAdminUser = (user) => {
  if (!user) {
    return {
      userId: 0,
      firstName: '',
      lastName: '',
      email: '',
      role: '',
      isBlocked: false,
    };
  }
  return {
    userId: user.userId ?? user.id ?? 0,
    firstName: user.firstName ?? '',
    lastName: user.lastName ?? '',
    email: user.email ?? '',
    role: user.role ?? '',
    isBlocked: user.isBlocked ?? false,
  };
};

const authBearerHeaders = () => {
  const sessionVal = getAdminXSessionHeader();
  return {
    Authorization: `Bearer ${getAdminToken() || ''}`,
    'Content-Type': 'application/json',
    'X-Session-Header': sessionVal,
    'x-session-header': sessionVal,
  };
};

const jsonHeaders = () => {
  const sessionVal = getAdminXSessionHeader();
  return {
    'Content-Type': 'application/json',
    'X-Session-Header': sessionVal,
    'x-session-header': sessionVal,
  };
};

export const authAPI = {
  login: (credentials) =>
    axios.post(`${BASE_URL}/Admin/Authentication/Login`, credentials, {
      headers: {
        'Content-Type': 'application/json',
        'X-Session-Header': getAdminXSessionHeader(),
        'x-session-header': getAdminXSessionHeader(),
      },
    }),
  signup: (data) => axios.post(`${BASE_URL}/Authentication/signup`, data, { headers: jsonHeaders() }),
  /** POST /api/Admin/Authentication/ForgetPassword — body: { email } */
  forgetPassword: (body) =>
    axios.post(`${BASE_URL}/Admin/Authentication/ForgetPassword`, body, { headers: jsonHeaders() }),
  /** POST /api/customer/Authentication/reset-password — body: { email, newPassword, otp } */
  resetPasswordWithOtp: (body) =>
    axios.post(`${BASE_URL}/customer/Authentication/reset-password`, body, { headers: jsonHeaders() }),
  /** POST /api/admin/Profile/change-password — body: { oldPassword, newPassword, confirmPassword } */
  changePassword: (body) =>
    axios.post(`${BASE_URL}/admin/Profile/change-password`, body, {
      headers: authBearerHeaders(),
    }),
  /** PUT /api/admin/Profile/display-name — body: { firstName, lastName } */
  updateDisplayName: (body) =>
    axios.put(`${BASE_URL}/admin/Profile/display-name`, body, {
      headers: authBearerHeaders(),
    }),
};

export const adminProfileAPI = {
  changePassword: (body) => authAPI.changePassword(body),
  updateDisplayName: (body) => authAPI.updateDisplayName(body),
};



export const contactClaimsAPI = {
  getContactClaims: (page = 0, pageSize = 10, search = '') => {
    let url = `/ContactClaims?Page=${page}&PageSize=${pageSize}`;
    const q = search != null ? String(search).trim() : '';
    if (q) {
      url += `&Search=${encodeURIComponent(q)}`;
    }
    return api.get(url);
  },
  updateClaimStatus: (claimId, status) => {
    return api.post(`/ContactClaims/${claimId}/status`, { status });
  }
};

export const userReportsAPI = {
  getUserReports: (page = 1, pageSize = 20, reportCategory = '') => {
    let url = `/Reports?page=${page}&pageSize=${pageSize}`;
    const category = String(reportCategory || '').trim();
    if (category && category.toLowerCase() !== 'all') {
      url += `&reportCategory=${encodeURIComponent(category)}`;
    }
    return api.get(url);
  },
  getProviderUserReports: (page = 1, pageSize = 20, search = '') => {
    let url = `/UserReports?page=${page}&pageSize=${pageSize}`;
    const q = String(search || '').trim();
    if (q) {
      url += `&search=${encodeURIComponent(q)}`;
    }
    return api.get(url);
  },
  getUserReportById: (id) => {
    return api.get(`/Reports/${id}`);
  },
  getProviderUserReportById: (id) => {
    return api.get(`/UserReports/${id}`);
  },
  getUserReportsByUserId: (userId, page = 1, pageSize = 20) => {
    return api.get(`/Users/${userId}/reports?page=${page}&pageSize=${pageSize}`);
  },
  deleteUserReport: (id) => {
    return api.delete(`/Reports/${id}`);
  },
  deleteProviderUserReport: (id) => {
    return api.delete(`/UserReports/${id}`);
  },
  setUserBlocked: (appUserId, isBlocked) => {
    return api.post(`/User/${appUserId}/SetBlocked`, { isBlocked });
  },
};

export const ratingModulesAPI = {
  getRatingModules: () => {
    return api.get('/RatingModules');
  },
  getRatingModuleByKey: (moduleKey) => {
    return api.get(`/RatingModules/${encodeURIComponent(moduleKey)}`);
  },
  createRatingModule: (data) => {
    return api.post('/RatingModules', data);
  },
  updateRatingModule: (moduleKey, data) => {
    return api.put(`/RatingModules/${encodeURIComponent(moduleKey)}`, data);
  },
};

export const customProviderNotificationsAPI = {
  /** GET /api/GetSettled/Admin/CustomProviderNotifications?page={page}&pageSize={pageSize} */
  getNotifications: (page = 1, pageSize = 20, search = '', status = '') => {
    let url = `/CustomProviderNotifications?page=${page}&pageSize=${pageSize}`;
    const q = String(search || '').trim();
    if (q) {
      url += `&search=${encodeURIComponent(q)}`;
    }
    const s = String(status || '').trim();
    if (s && s.toLowerCase() !== 'all') {
      url += `&status=${encodeURIComponent(s)}`;
    }
    return api.get(url);
  },
  getById: (id) => {
    return api.get(`/CustomProviderNotifications/${id}`);
  },
  updateStatus: (id, status, note = '') => {
    return api.post(`/CustomProviderNotifications/${id}/status`, { status, note });
  },
  approve: (id, note = '') => {
    return api.post(`/CustomProviderNotifications/${id}/status`, { status: 'Approved', note });
  },
  reject: (id, note = '') => {
    return api.post(`/CustomProviderNotifications/${id}/status`, { status: 'Rejected', note });
  },
  delete: (id) => {
    return api.delete(`/CustomProviderNotifications/${id}`);
  },
  create: (data) => {
    return api.post('/CustomProviderNotifications', data);
  },
};

export const categoryPushNotificationsAPI = {
  /** GET /api/GetSettled/Admin/CategoryPushNotifications/history?page={page}&pageSize={pageSize} */
  getHistory: (page = 1, pageSize = 20) => {
    return api.get(`/CategoryPushNotifications/history?page=${page}&pageSize=${pageSize}`);
  },
  /** POST /api/GetSettled/Admin/CategoryPushNotifications/send */
  send: (data) => {
    return api.post('/CategoryPushNotifications/send', data);
  },
  /** POST /api/GetSettled/Admin/CategoryPushNotifications/{id}/status */
  updateStatus: (id, status, note = '') => {
    return api.post(`/CategoryPushNotifications/${id}/status`, { status, note });
  },
};

export const providerNotificationsAPI = {
  getProviderNotifications: (page = 1, pageSize = 20, search = '', status = '') => {
    return customProviderNotificationsAPI.getNotifications(page, pageSize, search, status);
  },
  getProviderNotificationById: (id) => {
    return customProviderNotificationsAPI.getById(id);
  },
  updateProviderNotificationStatus: (id, status, note = '') => {
    return customProviderNotificationsAPI.updateStatus(id, status, note);
  },
  approveProviderNotification: (id, note = '') => {
    return customProviderNotificationsAPI.approve(id, note);
  },
  rejectProviderNotification: (id, note = '') => {
    return customProviderNotificationsAPI.reject(id, note);
  },
  deleteProviderNotification: (id) => {
    return customProviderNotificationsAPI.delete(id);
  },
  createProviderNotification: (data) => {
    return customProviderNotificationsAPI.create(data);
  },
};

export const providerApplicationsAPI = {
  getProviderApplications: (page = 1, pageSize = 20, search = '') => {
    let url = `/ProviderApplications?page=${page}&pageSize=${pageSize}`;
    const q = String(search || '').trim();
    if (q) {
      url += `&search=${encodeURIComponent(q)}`;
    }
    return api.get(url);
  },
  getProviderApplicationById: (applicationId) => {
    return api.get(`/ProviderApplications/${applicationId}`);
  },
};

export const userActivityAPI = {
  getActivityList: (module = 'Contact', page = 1, pageSize = 20, options = {}) => {
    let url = `/UserActivity/${encodeURIComponent(module)}?page=${page}&pageSize=${pageSize}`;
    if (options.search != null && String(options.search).trim()) {
      url += `&Search=${encodeURIComponent(String(options.search).trim())}`;
    }
    if (options.categoryId != null && options.categoryId !== '' && options.categoryId !== 'all') {
      url += `&CategoryId=${encodeURIComponent(options.categoryId)}`;
    }
    if (options.category != null && options.category !== '' && options.category !== 'all') {
      url += `&Category=${encodeURIComponent(options.category)}`;
    }
    if (options.typeId != null && options.typeId !== '' && options.typeId !== 'all') {
      url += '&TypeId=' + encodeURIComponent(options.typeId);
    }
    const dateVal = options.date || options.selectedDate || options.selectDate || options.Date;
    if (dateVal != null && String(dateVal).trim()) {
      const cleanDate = String(dateVal).trim();
      url += '&Date=' + encodeURIComponent(cleanDate) + '&SelectDate=' + encodeURIComponent(cleanDate) + '&SelectedDate=' + encodeURIComponent(cleanDate);
    }
    return api.get(url);
  },
  getSummary: (module = 'Contact', days = 30, period = '', groupBy = '', options = {}) => {
    let url = `/UserActivity/${encodeURIComponent(module)}/summary?days=${days}`;
    const p = period || options.period || groupBy;
    if (p) url += `&period=${encodeURIComponent(p)}`;
    if (groupBy || period) url += `&groupBy=${encodeURIComponent(groupBy || period)}`;
    if (options.year != null && options.year !== '' && options.year !== 'all') {
      url += `&year=${encodeURIComponent(options.year)}&Year=${encodeURIComponent(options.year)}`;
    }
    if (options.month != null && options.month !== '' && options.month !== 'all') {
      url += `&month=${encodeURIComponent(options.month)}&Month=${encodeURIComponent(options.month)}`;
    }
    if (options.week != null && options.week !== '' && options.week !== 'all') {
      url += `&week=${encodeURIComponent(options.week)}&Week=${encodeURIComponent(options.week)}`;
    }
    return api.get(url);
  },
  getTrend: (module = 'Contact', days = 7, period = '', groupBy = '', options = {}) => {
    let url = `/UserActivity/${encodeURIComponent(module)}/trend?days=${days}`;
    const p = period || options.period || groupBy;
    if (p) url += `&period=${encodeURIComponent(p)}`;
    if (groupBy || period) url += `&groupBy=${encodeURIComponent(groupBy || period)}`;
    if (options.year != null && options.year !== '' && options.year !== 'all') {
      url += `&year=${encodeURIComponent(options.year)}&Year=${encodeURIComponent(options.year)}`;
    }
    if (options.month != null && options.month !== '' && options.month !== 'all') {
      url += `&month=${encodeURIComponent(options.month)}&Month=${encodeURIComponent(options.month)}`;
    }
    if (options.week != null && options.week !== '' && options.week !== 'all') {
      url += `&week=${encodeURIComponent(options.week)}&Week=${encodeURIComponent(options.week)}`;
    }
    return api.get(url);
  },
  getTop: (module = 'Contact', limit = 10, days = 30, period = '', groupBy = '', options = {}) => {
    let url = `/UserActivity/${encodeURIComponent(module)}/top?limit=${limit}`;
    if (days) url += `&days=${days}`;
    const p = period || options.period || groupBy;
    if (p) url += `&period=${encodeURIComponent(p)}`;
    if (groupBy || period) url += `&groupBy=${encodeURIComponent(groupBy || period)}`;
    if (options.year != null && options.year !== '' && options.year !== 'all') {
      url += `&year=${encodeURIComponent(options.year)}&Year=${encodeURIComponent(options.year)}`;
    }
    if (options.month != null && options.month !== '' && options.month !== 'all') {
      url += `&month=${encodeURIComponent(options.month)}&Month=${encodeURIComponent(options.month)}`;
    }
    if (options.week != null && options.week !== '' && options.week !== 'all') {
      url += `&week=${encodeURIComponent(options.week)}&Week=${encodeURIComponent(options.week)}`;
    }
    return api.get(url);
  },
  getEntityDetail: (module = 'Contact', entityId, page = 1, pageSize = 20) => {
    return api.get(`/UserActivity/${encodeURIComponent(module)}/${entityId}?page=${page}&pageSize=${pageSize}`);
  },
  getUserActivities: (page = 1, pageSize = 20, searchProvider = '', searchService = '', startDate = '', endDate = '') => {
    let url = `/UserActivity?page=${page}&pageSize=${pageSize}`;
    if (searchProvider) url += `&provider=${encodeURIComponent(searchProvider)}`;
    if (searchService) url += `&service=${encodeURIComponent(searchService)}`;
    if (startDate) url += `&startDate=${encodeURIComponent(startDate)}`;
    if (endDate) url += `&endDate=${encodeURIComponent(endDate)}`;
    return api.get(url);
  },
  getUserActivityById: (id) => {
    return api.get(`/UserActivity/${id}`);
  },
  getUserActivityByProviderId: (providerId, page = 1, pageSize = 20) => {
    return api.get(`/UserActivity/Provider/${providerId}?page=${page}&pageSize=${pageSize}`);
  },
  getVisitors: (period = '', groupBy = '', days = null, month = null, year = null) => {
    let url = '/UserActivity/visitors';
    const params = [];
    if (period) params.push(`period=${encodeURIComponent(period)}`);
    if (groupBy) params.push(`groupBy=${encodeURIComponent(groupBy)}`);
    if (days != null && days !== '') params.push(`days=${days}`);
    if (month != null && month !== '' && month !== 'all') params.push(`month=${encodeURIComponent(month)}`);
    if (year != null && year !== '') params.push(`year=${encodeURIComponent(year)}`);
    if (params.length > 0) url += `?${params.join('&')}`;
    return api.get(url);
  },
};

export const referralLinkAPI = {
  /**
   * GET /api/GetSettled/Admin/ReferralLink?moduleKey=Contact&entityId=186
   */
  getReferralLink: (moduleKey, entityId) => {
    let url = `/ReferralLink?moduleKey=${encodeURIComponent(moduleKey)}`;
    if (entityId != null && entityId !== '') {
      url += `&entityId=${encodeURIComponent(entityId)}`;
    }
    return api.get(url);
  },
  /**
   * POST /api/GetSettled/Admin/ReferralLink
   * Body: { moduleKey, entityId, url, label, isActive }
   */
  createReferralLink: (data) => api.post('/ReferralLink', data),
  /**
   * PUT /api/GetSettled/Admin/ReferralLink
   * Body: { moduleKey, entityId, url, label, isActive }
   */
  updateReferralLink: (data) => api.put('/ReferralLink', data),
  /**
   * DELETE /api/GetSettled/Admin/ReferralLink?moduleKey=Contact&entityId=123
   */
  deleteReferralLink: (moduleKey, entityId) =>
    api.delete(`/ReferralLink?moduleKey=${encodeURIComponent(moduleKey)}&entityId=${encodeURIComponent(entityId)}`),
};

export const contactGoogleRatingAPI = {
  /**
   * GET /api/GetSettled/Admin/ContactGoogleRating
   * GET /api/GetSettled/Admin/ContactGoogleRating?contactId=123 — single rating.
   * GET /api/GetSettled/Admin/ContactGoogleRating — list ratings (optional isActive, page, pageSize).
   */
  getContactGoogleRating: (contactId = null, options = {}) => {
    let url = '/ContactGoogleRating';
    const params = [];
    if (contactId != null && contactId !== '') {
      params.push(`contactId=${encodeURIComponent(contactId)}`);
    }
    if (options.isActive !== undefined && options.isActive !== null && options.isActive !== '') {
      params.push(`isActive=${encodeURIComponent(options.isActive)}`);
    }
    if (options.page != null && options.page !== '') {
      params.push(`page=${encodeURIComponent(options.page)}`);
    }
    if (options.pageSize != null && options.pageSize !== '') {
      params.push(`pageSize=${encodeURIComponent(options.pageSize)}`);
    }
    if (params.length > 0) {
      url += `?${params.join('&')}`;
    }
    return api.get(url);
  },
  /**
   * POST /api/GetSettled/Admin/ContactGoogleRating
   * Body: { contactId, rating, totalRatings, reviewUrl, isActive }
   * Creates or updates Google rating for a contact (1 row per contactId).
   */
  createContactGoogleRating: (data) => api.post('/ContactGoogleRating', data),
  /**
   * PUT /api/GetSettled/Admin/ContactGoogleRating
   * Body: { contactId, rating, totalRatings, reviewUrl, isActive }
   * Updates Google rating for a contact.
   */
  updateContactGoogleRating: (data) => api.put('/ContactGoogleRating', data),
  /**
   * DELETE /api/GetSettled/Admin/ContactGoogleRating?contactId=123
   */
  deleteContactGoogleRating: (contactId) =>
    api.delete(`/ContactGoogleRating?contactId=${encodeURIComponent(contactId)}`),
};

export const contactInquiriesAPI = {
  /**
   * GET /api/GetSettled/Admin/ContactInquiries
   * Query params: page, pageSize, isReleased, isContactVerified, contactId, search
   */
  getContactInquiries: (options = {}) => {
    const {
      page = 1,
      pageSize = 20,
      isReleased,
      isContactVerified,
      contactId,
      search,
    } = options;
    const params = [];
    if (page != null && page !== '') {
      params.push(`page=${encodeURIComponent(page)}`);
    }
    if (pageSize != null && pageSize !== '') {
      params.push(`pageSize=${encodeURIComponent(pageSize)}`);
    }
    if (isReleased !== undefined && isReleased !== null && isReleased !== '' && isReleased !== 'all') {
      params.push(`isReleased=${encodeURIComponent(isReleased)}`);
    }
    if (isContactVerified !== undefined && isContactVerified !== null && isContactVerified !== '' && isContactVerified !== 'all') {
      params.push(`isContactVerified=${encodeURIComponent(isContactVerified)}`);
    }
    if (contactId != null && String(contactId).trim() !== '' && String(contactId).trim() !== 'all') {
      params.push(`contactId=${encodeURIComponent(String(contactId).trim())}`);
    }
    if (search != null && String(search).trim() !== '') {
      params.push(`search=${encodeURIComponent(String(search).trim())}`);
    }
    let url = '/ContactInquiries';
    if (params.length > 0) {
      url += `?${params.join('&')}`;
    }
    return api.get(url);
  },

  /**
   * POST /api/GetSettled/Admin/ContactInquiries/{inquiryId}/release
   * Releases an unverified contact inquiry and dispatches the inquiry email to the contact immediately.
   */
  releaseContactInquiry: (inquiryId) => {
    return api.post(`/ContactInquiries/${encodeURIComponent(inquiryId)}/release`);
  },
};

